// @flow
import * as React from 'react';
import styled from 'styled-components';
import { retinaBackgroundImage } from 'utils/style';
import { imageUrl } from 'utils/filestack';
import Link, { ConditionalLink } from 'components/Link';
import Divider from 'components/Divider';
import { SecondaryButton } from 'components/Button';
import Box from 'components/Box';
import Icon from 'components/Icon';
import Text, { H2, H3, BodySmall } from 'components/Text';
import type {
  CategoryHighlightBlockData,
  CategoryHighlightBlockItem
} from 'api/cms';
import { trackClicked } from 'utils/analytics';
import { colors, gutterWidth, transition } from '../../theme';

const { useState } = React;

const filestackTransform = (width: number, height: number) =>
  `resize=width:${width},height:${height},fit:clip,align:center/quality=value:70`;

const ImageBox = styled(Box)`
  ${props =>
    retinaBackgroundImage(
      imageUrl({
        handle: props.handle,
        transform: filestackTransform(360, 145)
      }),
      {
        filename2x: imageUrl({
          handle: props.handle,
          transform: filestackTransform(720, 290)
        }),
        include3x: false
      }
    )}
`;

const StyledLink = styled(Link)`
  color: ${props => props.color};
  svg {
    transition: transform ${transition};
    path,
    polyline {
      fill: currentColor;
    }
  }
  &:hover,
  &:focus {
    &,
    * {
      color: ${props => props.color};
      text-decoration: underline;
    }
    svg {
      transform: translateX(4px);
    }
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  &:hover,
  &:focus {
    background: white;
  }
`;

const Category = ({
  category,
  homePageUrl,
  blockIndex,
  pageUpdated,
  ...props
}: {
  homePageUrl: string,
  category: CategoryHighlightBlockItem,
  blockIndex: number,
  pageUpdated: string
}) => (
  <Box
    data-qa-id={`category-highlight-${category.slug}`}
    p={[gutterWidth / 4, null, gutterWidth / 2]}
    width={[1, 1, 1 / 3]}
    flex="0 0 auto"
    {...props}
  >
    <ConditionalLink
      link={
        homePageUrl
          ? `/${homePageUrl}?categories=${category.slug}&searchMode=true&pq=`
          : ''
      }
      onClick={() => {
        trackClicked('CMS Block', {
          block_position: blockIndex,
          block_type: 'Category Highlight',
          cta_type: 'Link',
          page_updated: pageUpdated
        });
      }}
    >
      {category.image ? (
        <ImageBox
          position="relative"
          height={145}
          width="100%"
          mb="12px"
          overflow="hidden"
          backgroundSize="cover"
          backgroundPosition="center"
          borderRadius={10}
          data-qa-id={`image-${category.slug}`}
          handle={category.image.handle}
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            px={24}
            py={18}
            width="100%"
            height="100%"
            display="flex"
            alignItems="flex-end"
            bg="rgba(72, 72, 72, 0.35)"
          >
            <H3 my={0} color="white" styleAs="h2">
              {category.name}
            </H3>
          </Box>
        </ImageBox>
      ) : (
        <H3 mb="4px">{category.name}</H3>
      )}
    </ConditionalLink>

    <BodySmall>{category.description}</BodySmall>

    <Divider light />

    {category.pages.map((page, index) => {
      const pageTitleWords = page.title.split(' ');
      return (
        <BodySmall
          styleAs="h5"
          lineHeight="1.5"
          mb="8px"
          key={`${page.id || index}`}
        >
          <StyledLink
            data-qa-id="articles-link"
            href={`/${page.url}`}
            onClick={() => {
              trackClicked('CMS Block', {
                block_position: blockIndex,
                block_type: 'Category Highlight',
                cta_type: 'Link',
                page_updated: pageUpdated
              });
            }}
          >
            {pageTitleWords.slice(0, pageTitleWords.length - 1).join(' ')}{' '}
            <Box as="span" display="inline-block">
              {pageTitleWords
                .slice(pageTitleWords.length - 1, pageTitleWords.length)
                .join(' ')}
              <Icon name="arrow" ml="8px" />
            </Box>
          </StyledLink>
        </BodySmall>
      );
    })}
    {homePageUrl && (
      <BodySmall styleAs="h5" lineHeight="1.5" mb="8px">
        <StyledLink
          color={colors.battleshipGrey}
          data-qa-id={`articles-link-${category.slug}`}
          href={`/${homePageUrl}?categories=${
            category.slug
          }&searchMode=true&pq=`}
          onClick={() => {
            trackClicked('CMS Block', {
              block_position: blockIndex,
              block_type: 'Category Highlight',
              cta_type: 'Link',
              page_updated: pageUpdated
            });
          }}
        >
          <Box as="span" display="inline-block">
            See all
            <Icon name="arrow" ml="8px" />
          </Box>
        </StyledLink>
      </BodySmall>
    )}
  </Box>
);

const CategoryHighlightBlock = ({
  data: { headline, categories },
  homePageUrl,
  blockIndex,
  pageUpdated
}: {
  data: CategoryHighlightBlockData,
  homePageUrl: string,
  subsiteId: string,
  locale: string,
  blockIndex: number,
  pageUpdated: string
}) => {
  const [showAll, setShowAll] = useState(false);

  return (
    <Box data-qa-id="cms-category-highlight">
      {headline && <H2 textAlign="center">{headline}</H2>}

      <Box
        m={[-gutterWidth / 4, null, -gutterWidth / 2]}
        display="flex"
        flexWrap="wrap"
      >
        {categories.slice(0, 3).map((category, index) => (
          <Category
            category={category}
            homePageUrl={homePageUrl}
            key={`${category.name}-${index}`} // eslint-disable-line react/no-array-index-key
            blockIndex={blockIndex}
            pageUpdated={pageUpdated}
          />
        ))}
      </Box>

      {categories.length > 3 && (
        <>
          {showAll && (
            <Box
              m={[-gutterWidth / 4, null, -gutterWidth / 2]}
              display="flex"
              flexWrap="wrap"
            >
              {categories.slice(3).map((category, index) => (
                <Category
                  category={category}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${category.name}-${index}`}
                  homePageUrl={homePageUrl}
                  blockIndex={blockIndex}
                  pageUpdated={pageUpdated}
                />
              ))}
            </Box>
          )}
          <Box position="relative">
            <Divider my={gutterWidth * 1.5} />
            <Box
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)"
            >
              <StyledSecondaryButton
                onClick={() => setShowAll(!showAll)}
                bg="white !important"
              >
                <Box component="span" mr="8px" whiteSpace="noWrap">
                  {showAll
                    ? getText('Show fewer categories')
                    : getText('Show all categories')}
                </Box>
                <Box
                  transform={showAll ? 'translateY(-9%)' : 'rotate(-90deg)'}
                  transition={`transform ${transition}`}
                  display="inline-block"
                  verticalAlign="middle"
                >
                  <Text styleAs="h4" lineHeight="1">
                    {showAll ? '–' : '+'}
                  </Text>
                </Box>
              </StyledSecondaryButton>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CategoryHighlightBlock;
